import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  FileText, Send, CheckCircle2, Lock, Download,
  Upload, AlertCircle, Loader2, Calendar, Search, RotateCcw,
  Eye, RefreshCw, Layers, ShieldCheck, Filter, ChevronRight, FileSpreadsheet
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtINR = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const _currentFy = () => {
  const d = new Date();
  const y = d.getFullYear();
  const startY = d.getMonth() >= 3 ? y : y - 1;
  return `${startY}-${String((startY + 1) % 100).padStart(2, '0')}`;
};

const StatCard = ({ icon: Icon, label, value, subtext, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700 icon:text-blue-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700 icon:text-emerald-600',
    amber: 'bg-amber-50 border-amber-200 text-amber-700 icon:text-amber-600',
    violet: 'bg-violet-50 border-violet-200 text-violet-700 icon:text-violet-600',
  }[color] || 'bg-blue-50 border-blue-200 text-blue-700 icon:text-blue-600';

  return (
    <div className={`p-4 rounded-xl border ${colors.split(' ')[0]} ${colors.split(' ')[1]}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <Icon className={`w-4 h-4 ${colors.split(' ')[3]}`} />
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-1 font-mono">{value ?? '0'}</p>
      {subtext && <p className="text-[11px] text-slate-500 mt-0.5">{subtext}</p>}
    </div>
  );
};

const TaxSheets = () => {
  const { user } = useAuth();
  const canEdit = ['tenant_admin', 'hr_admin', 'payroll_admin', 'payroll_processor', 'super_admin'].some(r =>
    (user?.roles || [user?.role]).includes(r)
  );

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [financialYear, setFinancialYear] = useState(_currentFy());
  const [fyOptions, setFyOptions] = useState([]);
  const [payrollPeriod, setPayrollPeriod] = useState('');
  const [payrollType, setPayrollType] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [taxSheets, setTaxSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [releaseModalOpen, setReleaseModalOpen] = useState(false);
  const [releaseResult, setReleaseResult] = useState(null);

  // Fetch Companies & FY Options
  useEffect(() => {
    axios.get(`${API_URL}/companies`, { withCredentials: true })
      .then(({ data }) => {
        setCompanies(data);
        setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
      }).catch(() => {});

    axios.get(`${API_URL}/tax-slab-master/meta/financial-years`, { withCredentials: true })
      .then(({ data }) => {
        if (data?.financial_years?.length) setFyOptions(data.financial_years);
      }).catch(() => {});
  }, [setSelectedCompany]);

  const fetchTaxSheets = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        company_id: selectedCompany,
        financial_year: financialYear,
        page: '1',
        limit: '300',
      });
      if (payrollPeriod) params.append('payroll_period', payrollPeriod);
      if (payrollType !== 'ALL') params.append('payroll_type', payrollType.toLowerCase());
      if (statusFilter !== 'ALL') params.append('status', statusFilter.toUpperCase());
      if (searchQuery.trim()) params.append('query', searchQuery.trim());

      const { data } = await axios.get(`${API_URL}/tax-sheets?${params.toString()}`, { withCredentials: true });
      setTaxSheets(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch tax sheets');
    }
    setLoading(false);
  }, [selectedCompany, financialYear, payrollPeriod, payrollType, statusFilter, searchQuery]);

  useEffect(() => { fetchTaxSheets(); }, [fetchTaxSheets]);

  // Statistics
  const stats = useMemo(() => {
    const total = taxSheets.length;
    const generated = taxSheets.filter(t => t.status === 'GENERATED' || t.status === 'READY').length;
    const released = taxSheets.filter(t => t.status === 'RELEASED').length;
    const portalVisible = taxSheets.filter(t => t.portal_visible && t.status === 'RELEASED').length;
    const legacyCount = taxSheets.filter(t => t.payroll_type === 'legacy').length;
    const regularCount = taxSheets.filter(t => t.payroll_type === 'regular').length;
    return { total, generated, released, portalVisible, legacyCount, regularCount };
  }, [taxSheets]);

  // Release Action (Single or Bulk)
  const handleRelease = async (runId = null, sheetId = null) => {
    setReleasing(true);
    setReleaseResult(null);
    try {
      let body = {};
      if (sheetId) {
        body = { tax_sheet_ids: [sheetId] };
      } else if (runId) {
        body = { run_id: runId };
      } else {
        // Bulk release by current filters / unique runs present in grid
        const runIds = Array.from(new Set(taxSheets.map(t => t.payroll_run_id).filter(Boolean)));
        if (runIds.length === 0) {
          toast.info('No eligible payroll runs found to release.');
          setReleasing(false);
          return;
        }
        let totalReleased = 0;
        let allFailures = [];
        for (const rid of runIds) {
          const res = await axios.post(`${API_URL}/tax-sheets/release`, { run_id: rid }, { withCredentials: true });
          totalReleased += res.data.affected || 0;
          if (res.data.failures?.length) {
            allFailures = [...allFailures, ...res.data.failures];
          }
        }
        setReleaseResult({
          ok: true,
          affected: totalReleased,
          failures: allFailures,
        });
        setReleaseModalOpen(true);
        toast.success(`Successfully released ${totalReleased} Tax Sheet(s)`);
        fetchTaxSheets();
        setReleasing(false);
        return;
      }

      const { data } = await axios.post(`${API_URL}/tax-sheets/release`, body, { withCredentials: true });
      setReleaseResult(data);
      setReleaseModalOpen(true);
      toast.success(`Released ${data.affected || 1} Tax Sheet(s)`);
      fetchTaxSheets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tax sheet release failed');
    }
    setReleasing(false);
  };

  const handleDownloadPdf = (taxSheetId) => {
    window.open(`${API_URL}/tax-sheets/${taxSheetId}/download`, '_blank');
  };

  const exportCSV = () => {
    if (!taxSheets.length) { toast.info('No data to export'); return; }
    const headers = [
      'Employee Code', 'Employee Name', 'Department', 'Designation',
      'Payroll Period', 'Financial Year', 'Payroll Type', 'Run ID',
      'Taxable Income', 'Tax Deducted', 'Status', 'Portal Visible', 'Released Date', 'Released By'
    ];
    const rows = taxSheets.map(t => [
      t.employee_code,
      `"${t.employee_name}"`,
      `"${t.department}"`,
      `"${t.designation}"`,
      t.payroll_period,
      t.financial_year,
      t.payroll_type?.toUpperCase(),
      t.payroll_run_id,
      t.taxable_income,
      t.tax_deducted,
      t.status,
      t.portal_visible ? 'YES' : 'NO',
      t.released_at ? new Date(t.released_at).toLocaleString() : '',
      t.released_by || ''
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Tax_Sheets_${financialYear}_${payrollPeriod || 'All'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="tax-sheets-module">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2.5">
              <FileText className="w-8 h-8 text-[#0052FF]" /> Tax Sheets Management
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-3xl">
              Dedicated repository for all employee Tax Computation Sheets across <strong>Regular Payroll</strong> and <strong>Legacy Payroll</strong>.
              Manage tax sheet records, verify portal delivery, release to Employee Portal, and inspect release history independently of Monthly Payroll Run.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedCompany}
              onChange={e => setSelectedCompany(e.target.value)}
              data-testid="ts-company-select"
              className="h-10 px-3 bg-white border border-slate-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-[#0052FF] outline-none"
            >
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex items-center gap-1 h-10 px-3 bg-white border border-slate-300 rounded-lg text-sm">
              <Calendar className="w-4 h-4 text-slate-400" />
              <select
                value={financialYear}
                onChange={e => setFinancialYear(e.target.value)}
                data-testid="ts-fy-select"
                className="bg-transparent outline-none font-medium text-slate-800"
              >
                {(fyOptions.length ? fyOptions : [_currentFy()]).map(fy => (
                  <option key={fy} value={fy}>FY {fy}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchTaxSheets}
              data-testid="ts-refresh-btn"
              className="h-10 px-3 border-slate-300 hover:bg-slate-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ts-stats">
          <StatCard icon={FileText} label="Total Records" value={stats.total} subtext={`Regular: ${stats.regularCount} · Legacy: ${stats.legacyCount}`} color="blue" />
          <StatCard icon={CheckCircle2} label="Generated / Ready" value={stats.generated} subtext="Stored in tax_sheets" color="amber" />
          <StatCard icon={ShieldCheck} label="Released" value={stats.released} subtext="Marked RELEASED" color="emerald" />
          <StatCard icon={Eye} label="Portal Available" value={stats.portalVisible} subtext="Visible in ESS Portal" color="violet" />
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-900 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#0052FF] shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-semibold text-sm text-slate-900">Authoritative Tax Sheet Store &amp; Delivery Engine</p>
            <p className="text-slate-700">
              Releasing a Tax Sheet updates its state in the authoritative <code className="bg-white/80 px-1 py-0.5 rounded text-blue-800 font-mono">tax_sheets</code> collection,
              sets <span className="font-semibold text-emerald-700">portal_visible = TRUE</span>, and supersedes any outdated runs for the same period.
              Employees view released tax sheets in <strong>Employee Portal → Pay &amp; Tax</strong>.
            </p>
          </div>
        </div>

        {/* Filter & Toolbar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="ts-toolbar">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  placeholder="Search code, name, dept..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  data-testid="ts-search-input"
                  className="w-full h-9 pl-9 pr-3 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:bg-white focus:border-[#0052FF]"
                />
              </div>
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 h-9 text-xs">
                <span className="text-slate-500 font-medium">Month:</span>
                <input
                  type="month"
                  value={payrollPeriod}
                  onChange={e => setPayrollPeriod(e.target.value)}
                  data-testid="ts-period-filter"
                  className="bg-transparent outline-none font-mono text-slate-800"
                />
                {payrollPeriod && (
                  <button onClick={() => setPayrollPeriod('')} className="text-slate-400 hover:text-slate-600 font-bold ml-1">✕</button>
                )}
              </div>
              <select
                value={payrollType}
                onChange={e => setPayrollType(e.target.value)}
                data-testid="ts-type-filter"
                className="h-9 px-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 outline-none focus:bg-white"
              >
                <option value="ALL">All Payroll Types</option>
                <option value="REGULAR">Regular Payroll</option>
                <option value="LEGACY">Legacy Payroll</option>
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                data-testid="ts-status-filter"
                className="h-9 px-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 outline-none focus:bg-white"
              >
                <option value="ALL">All Statuses</option>
                <option value="RELEASED">RELEASED</option>
                <option value="GENERATED">GENERATED</option>
                <option value="SUPERSEDED">SUPERSEDED</option>
                <option value="DRAFT">DRAFT</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportCSV}
                data-testid="ts-export-csv"
                className="h-9 text-xs border-slate-300"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> Export CSV
              </Button>
              {canEdit && (
                <Button
                  onClick={() => handleRelease()}
                  disabled={releasing || taxSheets.length === 0}
                  data-testid="ts-bulk-release-btn"
                  className="h-9 text-xs bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold"
                >
                  {releasing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Release Tax Sheets ({taxSheets.filter(t => t.status !== 'RELEASED').length})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="ts-table-container">
          {loading ? (
            <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#0052FF] mx-auto mb-2" /><p className="text-xs text-slate-500">Loading tax sheet records…</p></div>
          ) : taxSheets.length === 0 ? (
            <div className="py-20 text-center text-slate-500 space-y-2">
              <AlertCircle className="w-10 h-10 mx-auto text-slate-300" />
              <p className="text-sm font-semibold text-slate-700">No Tax Sheet records found</p>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                No records match the current filters. When a payroll run or legacy import is finalized or released, tax sheet records automatically populate here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left" data-testid="ts-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Employee</th>
                    <th className="px-3 py-3 font-semibold">Payroll Period</th>
                    <th className="px-3 py-3 font-semibold">Type</th>
                    <th className="px-3 py-3 font-semibold text-right">Taxable Income</th>
                    <th className="px-3 py-3 font-semibold text-right">Tax Deducted</th>
                    <th className="px-3 py-3 font-semibold text-center">Tax Sheet Status</th>
                    <th className="px-3 py-3 font-semibold text-center">Portal Delivery</th>
                    <th className="px-3 py-3 font-semibold">Release Date</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-sans">
                  {taxSheets.map(ts => {
                    const isReleased = ts.status === 'RELEASED';
                    const isLegacy = ts.payroll_type === 'legacy';
                    const isSuperseded = ts.status === 'SUPERSEDED';

                    return (
                      <tr key={ts._id} data-testid={`ts-row-${ts.employee_code || ts._id}`} className={`hover:bg-slate-50/80 transition-colors ${isSuperseded ? 'bg-slate-50/40 text-slate-400' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900 text-sm">{ts.employee_name || '—'}</div>
                          <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                            <span>{ts.employee_code}</span>
                            {ts.department && <span>· {ts.department}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-semibold text-slate-800 font-mono">{ts.payroll_period}</div>
                          <div className="text-[10px] text-slate-500">FY {ts.financial_year}</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            isLegacy ? 'bg-purple-100 text-purple-800 border border-purple-200' : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`}>
                            {ts.payroll_type || 'regular'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-slate-800">
                          {fmtINR(ts.taxable_income)}
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-rose-700">
                          {fmtINR(ts.tax_deducted)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            isReleased ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                            isSuperseded ? 'bg-slate-200 text-slate-600 border border-slate-300' :
                            'bg-amber-100 text-amber-800 border border-amber-300'
                          }`}>
                            {ts.status}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {ts.portal_visible && isReleased ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span> Available
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span> Hidden
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-600">
                          {ts.released_at ? (
                            <div>
                              <p className="font-medium">{new Date(ts.released_at).toLocaleDateString('en-IN')}</p>
                              <p className="text-[10px] text-slate-400">{new Date(ts.released_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadPdf(ts._id)}
                            data-testid={`ts-download-${ts.employee_code}`}
                            className="h-7 px-2 text-xs border-slate-300 text-slate-700"
                            title="View / Download Tax Sheet PDF"
                          >
                            <Download className="w-3.5 h-3.5 mr-1 text-[#0052FF]" /> PDF
                          </Button>
                          {canEdit && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRelease(ts.payroll_run_id, ts._id)}
                              disabled={releasing}
                              data-testid={`ts-release-${ts.employee_code}`}
                              className={`h-7 px-2 text-xs font-semibold ${
                                isReleased ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-[#0052FF] text-[#0052FF] hover:bg-blue-50'
                              }`}
                            >
                              {isReleased ? 'Re-release' : 'Release'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Release Status Result Modal */}
        {releaseModalOpen && releaseResult && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl animate-in fade-in zoom-in-95">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" /> Tax Sheet Release Complete
                </h3>
                <button onClick={() => setReleaseModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
              </div>

              <div className="space-y-3 text-sm">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-emerald-900">
                  <p className="font-semibold">Successfully Released: {releaseResult.affected || 0} Tax Sheets</p>
                  <p className="text-xs text-emerald-700 mt-0.5">Status set to RELEASED and portal_visible = TRUE for all targeted employees.</p>
                </div>

                {releaseResult.failures && releaseResult.failures.length > 0 ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-rose-700 text-xs uppercase tracking-wider">Failures ({releaseResult.failures.length}):</p>
                    <div className="max-h-48 overflow-y-auto divide-y divide-rose-100 border border-rose-200 rounded-lg bg-rose-50/50 p-2 text-xs">
                      {releaseResult.failures.map((f, i) => (
                        <div key={i} className="py-1.5">
                          <p className="font-semibold text-slate-800">{f.name} ({f.employee_code})</p>
                          <p className="text-rose-600">{f.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">All eligible employee tax sheets were released without errors.</p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={() => setReleaseModalOpen(false)} className="bg-[#0052FF] text-white">
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TaxSheets;
